import React, { useEffect, useState } from "react";
import { Typography } from "../Typography";
import { SvgIcon, SvgNames } from "../../assets/icons/SvgIcon";
import * as tokens from "../../design-tokens";
import { Button } from "../Button";
import "./Snackbar.css";

type SnackbarProps = {
  open: boolean;
  onClose: () => void;
  message: string;
  autoHideDuration?: number;
  type?: "success" | "error" | "warning" | "info";
  colorScheme?: "light" | "dark";
  action?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

const DEFAULT_HIDE_DURATION = 4000;

const snackIconType = {
  success: SvgNames.SuccessFilled,
  error: SvgNames.ErrorFilled,
  warning: SvgNames.WarningFilled,
  info: SvgNames.InfoFilled,
};

const Snackbar = ({
  open,
  action,
  message,
  type = "info",
  colorScheme = "light",
  children,
  autoHideDuration = DEFAULT_HIDE_DURATION,
  className,
  onClose,
}: SnackbarProps) => {
  const [classOpenController, setClassOpenController] = useState("");

  useEffect(() => {
    let timeout;
    if (open && autoHideDuration > 0) {
      timeout = setTimeout(() => {
        setClassOpenController("closed");
      }, autoHideDuration);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [autoHideDuration, open, onClose]);

  useEffect(() => {
    setClassOpenController(open ? "open" : "closed");
  }, [open]);

  const handleTransitionEnd = () => {
    if (classOpenController !== "closed") {
      return;
    }
    onClose();
  };

  if (!open) {
    return null;
  }

  const colorSchemeCode = `${colorScheme === "light" ? 20 : 90}`;
  const colorCode = `${type.toUpperCase()}_${colorSchemeCode}`;
  const color = tokens[colorCode];

  const iconName = snackIconType[type];

  return (
    <div
      data-testid="snackbar-container"
      onTransitionEnd={handleTransitionEnd}
      className={`snackbar ${classOpenController} ${type} ${colorScheme} ${
        className ?? ""
      }`}
    >
      <div className="snackbar-content">
        <div className="snackbar-type-icon">
          <SvgIcon color={color} width={32} height={32} name={iconName} />
        </div>
        <div className="snackbar-text">
          {children || (
            <Typography color={color} variant="span">
              {message}
            </Typography>
          )}
        </div>
        <div className="snackbar-action">{action}</div>
        <div className="snackbar-close">
          <Button
            size="xs"
            color="tertiary"
            iconMiddle={<SvgIcon color={color} name={SvgNames.Close} />}
            data-testid="snackbar-close-button"
            onClick={() => setClassOpenController("closed")}
          />
        </div>
      </div>
    </div>
  );
};

export type { SnackbarProps };
export { Snackbar };
